import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { AnimatePresence, motion } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import HomePage from './pages/HomePage';
import TestPage from './pages/TestPage';
import ErrorBoundary from './components/ErrorBoundary';
import { aviationDarkTheme } from './theme/aviationDarkTheme';

// Lazy-loaded pages for faster initial load and smaller main bundle
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Incidents = lazy(() => import('./pages/Incidents'));
const IncidentDetail = lazy(() => import('./pages/IncidentDetail'));
const IncidentEdit = lazy(() => import('./pages/IncidentEdit'));
const Reports = lazy(() => import('./pages/Reports'));
const Users = lazy(() => import('./pages/Users'));
const Categories = lazy(() => import('./pages/Categories'));
const Units = lazy(() => import('./pages/Units'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Configuration = lazy(() => import('./pages/Configuration'));
const Monitor = lazy(() => import('./pages/Monitor'));
const AviSkySync = lazy(() => import('./pages/AviSkySync'));
const ShiftHandover = lazy(() => import('./pages/ShiftHandover'));
const HandoverDetail = lazy(() => import('./pages/HandoverDetail'));
const LHDForms = lazy(() => import('./pages/LHDForms'));
const LHDFormEdit = lazy(() => import('./pages/LHDFormEdit'));
const LHDFormDetail = lazy(() => import('./pages/LHDFormDetail'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

const PageFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
    <CircularProgress />
  </Box>
);

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -10,
  },
};

const pageTransition = {
  type: 'tween' as const,
  ease: 'anticipate' as const,
  duration: 0.3,
};

// Animated page wrapper component
const AnimatedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
    style={{ width: '100%' }}
  >
    {children}
  </motion.div>
);

// Use the aviation dark theme
const theme = aviationDarkTheme;

// QueryClient: no cache so data is always fresh without hard refresh (permissions, list updates)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Always consider data stale → refetch on mount/focus (no hard refresh needed)
      gcTime: 2 * 60 * 1000, // Keep unused data 2 min for back-navigation, then evict
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 1,
    },
  },
});

// App content (routes)
const AppContent = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/test" element={<AnimatedPage><TestPage /></AnimatedPage>} />
        <Route path="/login" element={<AnimatedPage><Login /></AnimatedPage>} />
        <Route path="/reset-password" element={<AnimatedPage><ResetPassword /></AnimatedPage>} />
        <Route path="/home" element={<AnimatedPage><HomePage /></AnimatedPage>} />
        <Route path="/" element={<AnimatedPage><HomePage /></AnimatedPage>} />
        
        {/* Protected Routes */}
        <Route path="/app" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<AnimatedPage><Suspense fallback={<PageFallback />}><Dashboard /></Suspense></AnimatedPage>} />
          <Route path="incidents" element={<AnimatedPage><Suspense fallback={<PageFallback />}><Incidents /></Suspense></AnimatedPage>} />
          <Route path="incidents/new" element={<AnimatedPage><Suspense fallback={<PageFallback />}><IncidentEdit /></Suspense></AnimatedPage>} />
          <Route path="incidents/:id/edit" element={<AnimatedPage><Suspense fallback={<PageFallback />}><IncidentEdit /></Suspense></AnimatedPage>} />
          <Route path="incidents/:id" element={<AnimatedPage><Suspense fallback={<PageFallback />}><IncidentDetail /></Suspense></AnimatedPage>} />
          <Route path="lhd-forms" element={<AnimatedPage><Suspense fallback={<PageFallback />}><LHDForms /></Suspense></AnimatedPage>} />
          <Route path="lhd-forms/new" element={<AnimatedPage><Suspense fallback={<PageFallback />}><LHDFormEdit /></Suspense></AnimatedPage>} />
          <Route path="lhd-forms/:id/edit" element={<AnimatedPage><Suspense fallback={<PageFallback />}><LHDFormEdit /></Suspense></AnimatedPage>} />
          <Route path="lhd-forms/:id" element={<AnimatedPage><Suspense fallback={<PageFallback />}><LHDFormDetail /></Suspense></AnimatedPage>} />
          <Route path="shift-handover" element={<AnimatedPage><Suspense fallback={<PageFallback />}><ShiftHandover /></Suspense></AnimatedPage>} />
          <Route path="handover/:id" element={<AnimatedPage><Suspense fallback={<PageFallback />}><HandoverDetail /></Suspense></AnimatedPage>} />
          <Route path="reports" element={<AnimatedPage><Suspense fallback={<PageFallback />}><Reports /></Suspense></AnimatedPage>} />
          <Route path="monitor" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><Monitor /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="avisky-sync" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><AviSkySync /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><Users /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="categories" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><Categories /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="units" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><Units /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="profile" element={<AnimatedPage><Suspense fallback={<PageFallback />}><Profile /></Suspense></AnimatedPage>} />
          <Route path="settings" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><Settings /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="configuration" element={<ProtectedRoute requiredRole="admin"><AnimatedPage><Suspense fallback={<PageFallback />}><Configuration /></Suspense></AnimatedPage></ProtectedRoute>} />
          <Route path="unauthorized" element={<AnimatedPage><Suspense fallback={<PageFallback />}><Unauthorized /></Suspense></AnimatedPage>} />
        </Route>
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

function AppBody() {
  return (
    <>
      <Router>
        <AppContent />
      </Router>
      <ToastContainer
            position="top-right"
            theme="dark"
            autoClose={3500}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            limit={5}
        className="elog-toast-container"
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <AppBody />
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
